import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Grid, Paper, Typography, Stack, Button } from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RepeatOutlinedIcon from '@mui/icons-material/RepeatOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { PageHeader, StatCard, StatusChip, LoadingState } from '../components';
import { useProductStats } from '../hooks/useProducts';
import { useUnreadAlerts } from '../hooks/useAlerts';
import { useReservationsPaginated } from '../hooks/useStockReservations';
import { colors, formatCurrency } from '../constants';

const quickActions = [
  { label: 'New Product', icon: <AddCircleOutlineIcon />, route: '/inventory-management/products' },
  { label: 'Adjustment', icon: <SwapHorizOutlinedIcon />, route: '/inventory-management/stock-adjustments' },
  { label: 'New Invoice', icon: <DescriptionOutlinedIcon />, route: '/inventory-management/purchase-invoices/new' },
  { label: 'Transfers', icon: <RepeatOutlinedIcon />, route: '/inventory-management/stock-transfers' },
  { label: 'Counts', icon: <ChecklistOutlinedIcon />, route: '/inventory-management/inventory-counts' },
  { label: 'Reports', icon: <BarChartOutlinedIcon />, route: '/inventory-management/reports' },
  { label: 'Settings', icon: <SettingsOutlinedIcon />, route: '/inventory-management/settings' },
];

export default function Dashboard() {
  const history = useHistory();
  const { data: stats, isLoading: loadingStats } = useProductStats();
  const { data: alerts, isLoading: loadingAlerts } = useUnreadAlerts();
  const { data: reservations, isLoading: loadingReservations } =
    useReservationsPaginated({ status: 'pending', limit: 1 });

  const isLoading = loadingStats || loadingAlerts || loadingReservations;
  const pendingReservations = reservations?.pages?.[0]?.total ?? 0;
  const dash = (v) => (isLoading ? '--' : v);

  return (
    <Box>
      <PageHeader title="Dashboard" subtitle="Inventory overview" />

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<Inventory2OutlinedIcon />}
            label="Total Products"
            value={dash(stats?.totalProducts ?? 0)}
            onClick={() => history.push('/inventory-management/products')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<WarningAmberOutlinedIcon />}
            label="Low Stock"
            value={dash(stats?.lowStockCount ?? 0)}
            color={colors.warning}
            onClick={() => history.push('/inventory-management/products')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<PaidOutlinedIcon />}
            label="Inventory Cost"
            value={dash(formatCurrency(stats?.totalInventoryCost ?? 0))}
            color={colors.success}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<ErrorOutlineIcon />}
            label="No Cost Items"
            value={dash(stats?.noCostItemCount ?? 0)}
            color={colors.error}
            onClick={() => history.push('/inventory-management/products')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<NotificationsNoneOutlinedIcon />}
            label="Pending Alerts"
            value={dash(alerts?.length ?? 0)}
            color={colors.error}
            onClick={() => history.push('/inventory-management/alerts')}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            icon={<LockOutlinedIcon />}
            label="Reservations"
            value={dash(pendingReservations)}
            color={colors.info}
            onClick={() => history.push('/inventory-management/stock-reservations')}
          />
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{ mt: 3, p: 2.5, borderRadius: 3, border: `1px solid ${colors.border}` }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy, mb: 2 }}>
          Quick Actions
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {quickActions.map((action) => (
            <Button
              key={action.label}
              startIcon={action.icon}
              onClick={() => history.push(action.route)}
              variant="outlined"
              sx={{
                textTransform: 'none',
                color: colors.gray700,
                borderColor: colors.border,
                '&:hover': { borderColor: colors.primary, color: colors.primary },
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{ mt: 3, p: 2.5, borderRadius: 3, border: `1px solid ${colors.border}` }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy }}>
            Recent Alerts
          </Typography>
          <Button
            size="small"
            onClick={() => history.push('/inventory-management/alerts')}
            sx={{ textTransform: 'none', color: colors.primary }}
          >
            View All
          </Button>
        </Stack>
        {isLoading ? (
          <LoadingState height={120} />
        ) : !alerts || alerts.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.gray400, py: 2 }}>
            No unread alerts
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${colors.gray100}` }} />}>
            {alerts.slice(0, 5).map((alert) => (
              <Stack
                key={alert.uuid}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ py: 1.25 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: colors.gray700 }} noWrap>
                    {alert.message}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.gray400 }}>
                    {alert.productSku || alert.type}
                  </Typography>
                </Box>
                <StatusChip status={alert.severity} />
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
